import React, { useState, useEffect, useRef } from 'react';
import { TaskAttachment } from '../../../../types/Task';
import './TaskImageAttachments.css';

interface TaskImageAttachmentsProps {
  attachments: TaskAttachment[];
  onDeleteAttachment?: (attachmentId: number) => void;
  isTaskClosed?: boolean;
}

const TaskImageAttachments: React.FC<TaskImageAttachmentsProps> = ({ 
  attachments, 
  onDeleteAttachment,
  isTaskClosed = false 
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});
  const preloadedAttachmentsRef = useRef<string>('');

  // Filter to only show image attachments
  const imageAttachments = attachments.filter(attachment => 
    attachment.attachmentType === 'image' || 
    attachment.attachmentName.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)
  );

  // Cleanup blob URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(imageBlobUrls).forEach(blobUrl => {
        URL.revokeObjectURL(blobUrl);
      });
    };
  }, [imageBlobUrls]);

  // Preload images and create blob URLs
  const preloadImages = async (attachments: TaskAttachment[]) => {
    console.log('TaskImageAttachments: Preloading images for', attachments.length, 'attachments');
    const blobUrls: Record<string, string> = {};
    
    for (const attachment of attachments) {
      try {
        const blobUrl = await downloadImageAsBlob(attachment);
        blobUrls[attachment.id?.toString() || ''] = blobUrl;
      } catch (error) {
        console.error('TaskImageAttachments: Failed to preload image:', attachment.attachmentName, error);
        // Continue with other images even if one fails
      }
    }
    
    setImageBlobUrls(blobUrls);
    console.log('TaskImageAttachments: Preloaded', Object.keys(blobUrls).length, 'images');
  };

  // Download image data and create blob URL
  const downloadImageAsBlob = async (attachment: TaskAttachment): Promise<string> => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const cleanBaseUrl = baseUrl.replace('/api', '');
      const url = `${cleanBaseUrl}/api/attachments/download/${attachment.id}`;
      
      console.log('TaskImageAttachments: Downloading image from:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download image: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      console.log('TaskImageAttachments: Created blob URL for:', attachment.attachmentName);
      return blobUrl;
    } catch (error) {
      console.error('TaskImageAttachments: Error downloading image:', attachment.attachmentName, error);
      throw error;
    }
  };

  // Preload images when attachments change
  useEffect(() => {
    if (imageAttachments.length > 0) {
      // Create a unique key for this set of attachments to prevent duplicate preloading
      const attachmentKey = imageAttachments.map(a => `${a.id}-${a.attachmentName}`).join('|');
      
      if (preloadedAttachmentsRef.current !== attachmentKey) {
        console.log('TaskImageAttachments: Preloading new set of attachments');
        preloadedAttachmentsRef.current = attachmentKey;
        preloadImages(imageAttachments);
      }
    }
  }, [imageAttachments.length, imageAttachments.map(a => a.id).join(',')]);

  // Generate server URL for image display (fallback)
  const getImageUrl = (attachment: TaskAttachment) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const cleanBaseUrl = baseUrl.replace('/api', '');
    const url = `${cleanBaseUrl}/api/attachments/download/${attachment.id}`;
    return url;
  };

  const handleImageClick = (attachment: TaskAttachment) => {
    const imageUrl = imageBlobUrls[attachment.id?.toString() || ''] || getImageUrl(attachment);
    setSelectedImage(imageUrl);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  if (imageAttachments.length === 0) {
    return null;
  }

  return (
    <div className="task-image-attachments">
      <h6 className="tw-mb-3">Images ({imageAttachments.length})</h6>
      <div className="task-images-grid">
        {imageAttachments.map((attachment) => (
          <div 
            key={attachment.id} 
            className="task-image-item"
            onClick={() => handleImageClick(attachment)}
          >
            <img
              src={imageBlobUrls[attachment.id?.toString() || ''] || getImageUrl(attachment)}
              alt={attachment.attachmentName}
              className="task-image-thumbnail"
              onLoad={() => console.log('TaskImageAttachments: Image loaded successfully:', attachment.attachmentName)}
              onError={(e) => console.error('TaskImageAttachments: Image failed to load:', attachment.attachmentName, e)}
            />
            <div className="task-image-info">
              <span className="task-image-filename">{attachment.attachmentName}</span>
              {attachment.attachmentNotes && (
                <span 
                  className="task-image-notes" 
                  title={attachment.attachmentNotes}
                >
                  {attachment.attachmentNotes}
                </span>
              )}
            </div>
            {!isTaskClosed && onDeleteAttachment && (
              <button
                className="task-image-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteAttachment(attachment.id ?? 0);
                }}
                title="Delete image"
              >
                <i className="bi bi-trash"></i>
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div className="task-image-modal" onClick={closeModal}>
          <div className="task-image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="task-image-modal-close" onClick={closeModal}>
              ×
            </button>
            <img
              src={selectedImage}
              alt="Full size"
              className="task-image-modal-image"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskImageAttachments;
